import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useCallback } from "react";
import {
  DestinationStopType,
  OriginStopType,
} from "../../../generated/graphql";
import { type AddressFormState } from "../api-order-form";
import useApiOrderForm from "../hooks/use-api-order-form";
import ApiOrderDetailsSection from "./api-order-details-section";
import ApiOrderPackagesSection from "./api-order-packages-section";
import ApiOrderStopSection from "./api-order-stop-section";
import ApiOrderTargetSection from "./api-order-target-section";
import OrderOutcomeAlert from "./order-outcome-alert";

const styles = {
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
  } satisfies SxProps<Theme>,
};

const ORIGIN_STOP_TYPES = Object.values(OriginStopType);
const DESTINATION_STOP_TYPES = Object.values(DestinationStopType);

type ApiOrderPanelProps = {
  /** Disables submission, used when the selected environment is production. */
  readonly disabled: boolean;
};

/**
 * The typed API-order tab: pick a company and billing contact, describe the
 * order, and push it through the external `POST /v2/orders` create path so it
 * lands with `OrderSource.API`.
 *
 * Submitting the same HAWB twice for one contact is the intended way to observe
 * duplicate-order merging — the second submission reports the surviving order
 * rather than failing.
 */
const ApiOrderPanel = ({ disabled }: ApiOrderPanelProps) => {
  const {
    form,
    companyOptions,
    contactOptions,
    serviceOptions,
    companiesLoading,
    contactsLoading,
    companiesError,
    contactsError,
    submitting,
    outcome,
    error,
    setField,
    setCompany,
    setBillingContact,
    setPackage,
    addPackage,
    removePackage,
    submit,
    reset,
  } = useApiOrderForm();

  const handleIncludeOriginChange = useCallback(
    (next: boolean) => {
      setField("includeOrigin", next);
    },
    [setField],
  );

  const handleOriginStopTypeChange = useCallback(
    (next: string) => {
      setField("originStopType", next as OriginStopType);
    },
    [setField],
  );

  const handleOriginAddressChange = useCallback(
    (next: AddressFormState) => {
      setField("originAddress", next);
    },
    [setField],
  );

  const handleIncludeDestinationChange = useCallback(
    (next: boolean) => {
      setField("includeDestination", next);
    },
    [setField],
  );

  const handleDestinationStopTypeChange = useCallback(
    (next: string) => {
      setField("destinationStopType", next as DestinationStopType);
    },
    [setField],
  );

  const handleDestinationAddressChange = useCallback(
    (next: AddressFormState) => {
      setField("destinationAddress", next);
    },
    [setField],
  );

  const handleSubmitClick = useCallback(() => {
    void submit();
  }, [submit]);

  return (
    <Box sx={styles.panel}>
      {companiesError !== undefined && (
        <Alert severity="error">{companiesError.message}</Alert>
      )}
      {contactsError !== undefined && (
        <Alert severity="error">{contactsError.message}</Alert>
      )}

      <ApiOrderTargetSection
        billingContactUuid={form.billingContactUuid}
        companiesLoading={companiesLoading}
        companyOptions={companyOptions}
        companyUuid={form.companyUuid}
        contactOptions={contactOptions}
        contactsLoading={contactsLoading}
        serviceLevelUuid={form.serviceLevelUuid}
        serviceOptions={serviceOptions}
        setField={setField}
        onBillingContactChange={setBillingContact}
        onCompanyChange={setCompany}
      />

      <ApiOrderDetailsSection
        houseAirwayBillNumber={form.houseAirwayBillNumber}
        masterAirwayBillOfLadingNumber={form.masterAirwayBillOfLadingNumber}
        paymentMethod={form.paymentMethod}
        setField={setField}
      />

      <ApiOrderStopSection
        address={form.originAddress}
        included={form.includeOrigin}
        label="Origin stop"
        stopType={form.originStopType}
        stopTypeLabel="Origin stop type"
        stopTypeOptions={ORIGIN_STOP_TYPES}
        onAddressChange={handleOriginAddressChange}
        onIncludedChange={handleIncludeOriginChange}
        onStopTypeChange={handleOriginStopTypeChange}
      />

      <ApiOrderStopSection
        address={form.destinationAddress}
        included={form.includeDestination}
        label="Destination stop"
        stopType={form.destinationStopType}
        stopTypeLabel="Destination stop type"
        stopTypeOptions={DESTINATION_STOP_TYPES}
        onAddressChange={handleDestinationAddressChange}
        onIncludedChange={handleIncludeDestinationChange}
        onStopTypeChange={handleDestinationStopTypeChange}
      />

      <ApiOrderPackagesSection
        packages={form.packages}
        onPackageAdd={addPackage}
        onPackageChange={setPackage}
        onPackageRemove={removePackage}
      />

      <Divider />

      <Stack direction="row" spacing={2} alignItems="center">
        <Button
          disabled={disabled || submitting}
          variant="contained"
          onClick={handleSubmitClick}
        >
          Create API order
        </Button>
        <Button disabled={submitting} onClick={reset}>
          Reset form
        </Button>
      </Stack>

      <OrderOutcomeAlert error={error} outcome={outcome} />
    </Box>
  );
};

export default ApiOrderPanel;
